import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { IReview } from '../../../../shared/models/review';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { ReviewService } from '../../../../shared/services/review.service';
import { EStage, EStatus, EYesNo } from '../../../../shared/models/types.enum';
import { AssessCaseService } from '../../../../shared/services/assessCase.service';
import { IRoarAssignmentForm } from '../../../../shared/models/roarAssignmentForm';
import { RoarAssignmentFormService } from '../../../../shared/services/roarAssignmentForm.service';
import { ReviewRoarComponent } from '../../review-content/review-roar/review-roar.component';
import { ReviewRoarActivityComponent } from '../../review-content/review-roar-activity/review-roar-activity.component';
import { ReviewRoarActivityReviewComponent } from '../../review-content/review-roar-activity-review/review-roar-activity-review.component';
import { ReviewRmsPlanChecklistComponent } from '../../review-content/review-rms-plan-checklist/review-rms-plan-checklist.component';
import { ReviewRmsPlanScheduleComponent } from '../../review-content/review-rms-plan-schedule/review-rms-plan-schedule.component';
import { ReviewRmsPlanSummaryComponent } from '../../review-content/review-rms-plan-summary/review-rms-plan-summary.component';
import { RejectionReasonComponent } from '../../rejection-reason/rejection-reason.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'plans-review-modal',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    SpinnerComponent,
    ReviewRmsPlanChecklistComponent,
    ReviewRoarComponent,
    ReviewRoarActivityComponent,
    ReviewRoarActivityReviewComponent,
    ReviewRmsPlanScheduleComponent,
    ReviewRmsPlanSummaryComponent,
    RejectionReasonComponent
  ],
  templateUrl: './plans-review-modal.component.html',
  styleUrl: './plans-review-modal.component.css'
})
export class PlansReviewModalComponent {
  
  @Input() taskId: string | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSend = new EventEmitter<{type: string, message: string}>();

  icons = Icons;
  statusEnum = EStatus;
  stageEnum = EStage;
  yesNo = EYesNo;

  assessCase: any | null = null;
  currentBuildingId: string | null = null;
  currentForm: { name: string, title: string } = { name: '', title: '' };
  allRoarAssignments: IRoarAssignmentForm[] = [];
  formsList: any[] = [];

  loading: boolean = false;
  showRejectionReason: boolean = false;

  forms = [ 
    { name: 'rms_plan_checklist', title: 'RMS Plan Checklist' },
    { name: 'roar_assignment_form', title: 'Roar Assignment Form' },
    { name: 'rms_plan_review_schedule', title: 'RMS Plan Review Schedule' },
    { name: 'rms_plan_summary', title: 'RMS Plan Summary' }
  ]

  constructor(
    private userDataService: UserDataService,
    private assessCaseService: AssessCaseService,
    private reviewService: ReviewService,
    private roarAssignmentFormService: RoarAssignmentFormService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();

    this.loadAssessCases();

    if (this.taskId) {
      this.loadAllRoarAssignments(this.taskId);
    }
  }

  loadAssessCases(): void {
    if (this.taskId) {
      this.assessCaseService.getAssessCaseByCaseId(this.taskId).subscribe({
        next: (response) => {
          this.assessCase = response;
          this.forms.forEach((form) => {
            if (this.assessCase[form.name] === this.statusEnum.ReviewPending) {
              this.formsList.push(form);
            }
          });
          this.currentForm = this.formsList[0] || { name: '', title: '' };
        },
        error: (error) => {
          console.error('Error loading assess case:', error);
          this.onSend.emit({type: 'failed', message: error.message});
        }
      });
    }
  }

  loadAllRoarAssignments(caseId: string): void {
    if (this.currentBuildingId && caseId) {
      this.loading = true;
      this.roarAssignmentFormService.getRoarAssignmentFormByCaseId(caseId).subscribe({
        next: (response) => {
          this.loading = false;
          this.allRoarAssignments = response;
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching ROAR assignments:', error);
        }
      });
    }
  }

  handleRejectionSubmit(noteFormValue: { note: string }): void {
    const note = noteFormValue.note;
    this.approvePlans(this.yesNo.No, note);
  }

  approvePlans(decision: string, note?: string): void {
    if (this.taskId) {
      this.loading = true;
      const data: IReview = {
        case_id: this.taskId,
        building_id: Number(this.currentBuildingId),
        section_review_type: this.stageEnum.Plans,
        approve: decision,
        note: note || ''
      };
      this.reviewService.reviewCompleted(data, this.currentForm.name).subscribe({
        next: (response) => {
          this.loading = false;
          this.showRejectionReason = false;
          // remove the form object out of the formsList array
          this.formsList = this.formsList.filter(form => form.name !== this.currentForm.name);
          this.currentForm = this.formsList[0] || { name: '', title: '' };
          if (this.formsList.length === 0) {
            this.onSend.emit({type: 'success', message: 'This occurred after requesting a review.'});
          } else {
            this.toastr.success('Saved successful!', 'Success');
          }
        },
        error: (error) => {
          this.loading = false;
          this.showRejectionReason = false;
          console.error('Error completing review:', error);
          if (this.formsList.length === 0) {
            this.onSend.emit({type: 'failed', message: error.message});
          } else {
            this.toastr.error(error.message, 'Error'); 
          }
        }
      });
    }
  }

  handleCloseReviewModal(): void {
    this.taskId = null;
    this.onClose.emit();
  }

}
